import { extendTheme } from "@mui/material/styles";

/**
 * Kernel, powered by Lopie One.
 *
 * ## Brand colour is not a semantic colour
 *
 * The build map already uses green for "built", amber for "partly built" and
 * red for "blocked", and this console uses the same three for permitted,
 * needs-attention and refused. If the brand accent were also green, a green
 * chip would mean "this is permitted" in one place and "this is Kernel" in
 * another, and the semantic one would stop reading as a signal.
 *
 * So the brand is teal and the semantics are left alone. Teal also happens to
 * suit the subject — it reads as instrument rather than as marketing, and it
 * holds contrast on both a near-white and a near-black ground, which a lighter
 * accent would not.
 *
 * ## Neutrals are chosen, not inherited
 *
 * A pure mid-grey reads as nobody decided. These carry a slight cool bias
 * toward the accent, which is what makes a dense table of mostly-grey read as
 * designed rather than as unstyled.
 *
 * ## v9 notes
 *
 * `extendTheme` with palettes under `colorSchemes`, not `createTheme`. The
 * provider no longer takes `enableColorScheme` — that moved to `CssBaseline`.
 */

const brand = {
  50: "#e6f4f6",
  100: "#bde3e8",
  200: "#8ecdd6",
  300: "#5ab5c2",
  400: "#31a0b1",
  500: "#0e7490",
  600: "#0b5f78",
  700: "#084a5e",
  800: "#063845",
  900: "#04262f",
};

/** Named for what they mean, not for what colour they are.
 *
 * A screen writing `success.main` for a permitted tag is a screen that will
 * eventually use `success.main` for something else and quietly overload it.
 * These are the four states governance actually has. */
const semantic = {
  light: {
    permitted: "#1f7a3d",
    refused: "#a32b2b",
    attention: "#b3730b",
    unattended: "#5b53b8",
    stale: "#6a706e",
  },
  dark: {
    permitted: "#4fb474",
    refused: "#e06666",
    attention: "#d9962a",
    unattended: "#9a92e8",
    stale: "#9aa1a0",
  },
};

const theme = extendTheme({
  cssVarPrefix: "kernel",

  // Without this the toggle in the app bar is decorative, and it was.
  //
  // The default selector is `media`, which means the dark palette is emitted
  // only inside `@media (prefers-color-scheme: dark)`. Following the system is
  // then the *only* thing that works: `setMode("dark")` writes `mui-mode` to
  // localStorage, `useColorScheme()` cheerfully reports `dark`, and not one
  // CSS variable moves, because nothing stamps a selector for the dark block
  // to match. Found in LO-31, by a mounted micro-app going dark while the
  // console around it stayed light — the remote was reading the mode the host
  // reported, and the host was not applying it.
  //
  // `data` stamps `data-light` / `data-dark` on `<html>`, which is what the
  // generated CSS keys off. "System" is not a fourth state in the DOM — MUI
  // resolves it and stamps whichever the machine actually is — so all three
  // states the toggle offers now do something, rather than one.
  colorSchemeSelector: "data",

  colorSchemes: {
    light: {
      palette: {
        primary: { main: brand[500], light: brand[300], dark: brand[700] },
        background: { default: "#f6f8f8", paper: "#ffffff" },
        text: { primary: "#12191a", secondary: "#5a6566" },
        divider: "#dfe5e5",
        kernel: semantic.light,
      },
    },
    dark: {
      palette: {
        primary: { main: brand[300], light: brand[200], dark: brand[600] },
        background: { default: "#0e1416", paper: "#151d1f" },
        text: { primary: "#e7eded", secondary: "#98a5a5" },
        divider: "#26312f",
        kernel: semantic.dark,
      },
    },
  },

  shape: { borderRadius: 8 },

  typography: {
    // System stack. A webfont on an internal console is a network request
    // between an operator and their own data, paid on every cold load.
    fontFamily:
      'system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    // Dense by default. This is a tool, and a tool that needs scrolling to
    // show twelve rows is a tool people stop opening.
    fontSize: 14,
    h1: { fontSize: "1.5rem", fontWeight: 600, letterSpacing: "-0.01em" },
    h2: { fontSize: "1.125rem", fontWeight: 600 },
    overline: {
      fontSize: "0.6875rem",
      fontWeight: 600,
      letterSpacing: "0.08em",
      lineHeight: 1.6,
    },
    // Ids, tokens and timestamps. Anything you might compare character by
    // character wants a face where you can.
    mono: {
      fontFamily:
        'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
      fontSize: "0.8125rem",
    },
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        // Wide tables scroll inside their own container; the page never
        // scrolls sideways.
        body: { overflowX: "hidden" },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: { root: { textTransform: "none" } },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 500 },
        // Chips carry tags and ids. Both are compared, not read.
        labelSmall: { fontSize: "0.75rem" },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { paddingTop: 8, paddingBottom: 8 },
        head: {
          fontSize: "0.6875rem",
          fontWeight: 700,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          whiteSpace: "nowrap",
        },
      },
    },
    MuiTooltip: {
      defaultProps: { arrow: true },
    },
    MuiLink: {
      defaultProps: { underline: "hover" },
    },
  },
});

export default theme;
